// «Consejo de Crecimiento» en la app Agentes — solo personas admin.
//
// Backend (mcp_django #573, apps/marketing/consejo):
//   GET  /api/v1/admin/marketing/consejo         → {activado, miembros[], skills[]}
//   POST /api/v1/admin/marketing/consejo/activar → mismo shape (idempotente; un
//        agente recibe 403: solo una persona admin del dashboard puede activarlo)
// La org sale del JWT (el proxy la inyecta); aquí no se elige ni se adivina org.
// El @handle de cada miembro lo calcula SOLO el backend: aquí solo se muestra.
// Al activar se invalida la lista de agentes para que aparezcan en el chat (@).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Loader2, RefreshCw, Sparkles, Users } from 'lucide-react';

import { djangoApiCall } from '@/lib/api';
import { httpStatus } from '@/modules/orion-shell/data';
import { useCopy } from '@/components/assistant/clipboard';

export interface ConsejoMiembro {
  key: string;
  name: string;
  handle: string;
  icon?: string;
  description?: string;
  assistant_id?: string | null;
  activo: boolean;
  al_dia: boolean;
}

export interface ConsejoEstado {
  activado: boolean;
  miembros: ConsejoMiembro[];
  skills: { slug: string; instalada: boolean }[];
}

const QK = ['agents-os', 'consejo'];

const getConsejo = () => djangoApiCall<ConsejoEstado>('/admin/marketing/consejo');
const activarConsejo = () => djangoApiCall<ConsejoEstado>('/admin/marketing/consejo/activar', { method: 'POST' });

function ejemplo(miembros: ConsejoMiembro[]): string {
  const h = (k: string, fallback: string) => `@${miembros.find((m) => m.key === k)?.handle || fallback}`;
  return (
    `Genera una campaña de marketing para este mes. Pregúntale a ${h('consejo.analista_marca', 'analistademarca')} ` +
    `qué opina sobre la marca y a ${h('consejo.analista_mercado', 'analistademercado')} qué opina del mercado, ` +
    `y que ${h('consejo.finanzas', 'finanzas')} apruebe el presupuesto usando la skill de finanzas para marketing.`
  );
}

function errorText(err: unknown, accion: 'cargar' | 'activar'): string {
  const s = httpStatus(err);
  if (s === 403) return 'Solo una persona admin de la organización puede activar el Consejo.';
  if (s === 404) return 'El Consejo aún no está disponible en este servidor. Vuelve a intentarlo después de la próxima actualización.';
  return accion === 'cargar'
    ? 'No se pudo consultar el Consejo. Intenta de nuevo en un momento.'
    : 'No se pudo activar el Consejo. Intenta de nuevo; si sigue fallando, avísale a soporte.';
}

export function ConsejoCard({ onActivated }: { onActivated?: () => void } = {}) {
  const qc = useQueryClient();
  const q = useQuery<ConsejoEstado>({ queryKey: QK, queryFn: getConsejo, retry: 1, staleTime: 60_000 });
  const m = useMutation({
    mutationFn: activarConsejo,
    onSuccess: (data) => {
      qc.setQueryData(QK, data);
      void qc.invalidateQueries({ queryKey: ['orion-shell', 'agents'] });
      onActivated?.();
    },
  });
  const [copied, copy] = useCopy();
  const [showAll, setShowAll] = useState(false);

  const data = q.data;
  const miembros = data?.miembros ?? [];
  const activos = miembros.filter((x) => x.activo).length;
  const desactualizados = miembros.filter((x) => x.activo && !x.al_dia).length;
  const activado = !!data?.activado;
  const texto = ejemplo(miembros);
  const visibles = showAll ? miembros : miembros.slice(0, 8);

  return (
    <section
      aria-labelledby="consejo-title"
      className="orion-card mb-4 min-w-0 overflow-hidden rounded-[20px] border border-border bg-card text-card-foreground"
    >
      <header className="flex flex-wrap items-start gap-3 px-4 pb-2 pt-4 sm:px-5 sm:pt-5">
        <span aria-hidden className="orion-tone tone-violet grid h-11 w-11 shrink-0 place-items-center rounded-xl">
          <Users size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="consejo-title" className="text-[15px] font-semibold tracking-tight text-foreground">
            Consejo de Crecimiento
          </h2>
          <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
            {activado
              ? 'Menciona a un miembro con @ en el chat: Orión le pide su opinión y junta las respuestas en una sola propuesta.'
              : '8 especialistas de marketing (estrategia, datos, mercado, marca, finanzas, creativo, medios y un abogado del diablo) que Orión consulta cuando los mencionas con @.'}
          </p>
        </div>
        {data && (
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ${
              activado ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
            }`}
          >
            {activado ? <Check size={12} aria-hidden /> : null}
            {activado ? 'Activo' : activos > 0 ? `${activos} de ${miembros.length} activos` : 'Sin activar'}
          </span>
        )}
      </header>

      <div className="px-4 pb-4 sm:px-5 sm:pb-5">
        {q.isLoading && (
          <p role="status" className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 size={15} className="animate-spin" aria-hidden /> Revisando el Consejo…
          </p>
        )}

        {q.isError && !data && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <p role="alert" className="min-w-0 flex-1 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {errorText(q.error, 'cargar')}
            </p>
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-foreground hover:bg-muted"
            >
              <RefreshCw size={14} aria-hidden /> Reintentar
            </button>
          </div>
        )}

        {data && !activado && (
          <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center">
            <ul className="flex min-w-0 flex-1 flex-wrap gap-1.5" aria-label="Miembros del Consejo">
              {miembros.map((x) => (
                <li key={x.key} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  <span aria-hidden>{x.icon}</span> {x.name}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => m.mutate()}
              disabled={m.isPending}
              className="os-btn-brand inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-semibold"
            >
              {m.isPending ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Sparkles size={15} aria-hidden />}
              {m.isPending ? 'Activando…' : activos > 0 ? 'Completar el Consejo' : 'Activar el Consejo'}
            </button>
          </div>
        )}

        {data && !activado && (
          <p className="mt-2 text-xs text-muted-foreground">
            Crea los 8 agentes en tu organización (visibles para todo el equipo) e instala la skill «Finanzas para marketing». No
            publica ni gasta nada: los agentes solo opinan y tú decides.
          </p>
        )}

        {m.isError && (
          <p role="alert" className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {errorText(m.error, 'activar')}
          </p>
        )}

        {data && activado && (
          <>
            <ul className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2 lg:grid-cols-4" aria-label="Miembros del Consejo">
              {visibles.map((x) => (
                <li key={x.key} className="flex min-w-0 items-start gap-2.5 rounded-xl border border-border bg-background/40 px-3 py-2.5">
                  <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-base">
                    {x.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold text-foreground">{x.name}</span>
                    <span className="block truncate text-xs text-primary">@{x.handle}</span>
                  </span>
                </li>
              ))}
            </ul>
            {miembros.length > 8 && (
              <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 text-xs font-semibold text-primary hover:underline">
                {showAll ? 'Ver menos' : `Ver los ${miembros.length}`}
              </button>
            )}

            <div className="mt-3 rounded-xl border border-dashed border-border bg-muted/30 p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Prueba en el chat</p>
              <p className="break-words text-[13px] leading-relaxed text-foreground">{texto}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => copy(texto)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 text-[13px] font-medium text-primary hover:bg-primary/15"
                >
                  {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                  {copied ? 'Copiado' : 'Copiar ejemplo'}
                </button>
                <span aria-live="polite" className="sr-only">{copied ? 'Ejemplo copiado' : ''}</span>
                {desactualizados > 0 && (
                  <button
                    type="button"
                    onClick={() => m.mutate()}
                    disabled={m.isPending}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {m.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <RefreshCw size={14} aria-hidden />}
                    Poner al día ({desactualizados})
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default ConsejoCard;
